import { getRole } from '@/store/selectors';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { getAllTasksFromRequirements } from '@/utilities/transformRequirements';
import { Badge, Box, Button, Flex, Stack, useDisclosure } from '@chakra-ui/react';
import { faCirclePlus, faFilePlus, faPen } from '@fortawesome/pro-regular-svg-icons';
import { OfferTooltipLabels } from 'apply-data';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
  CustomHeading,
  getStatusByConditionType,
  IconButtonWithTooltip,
  RoundBadgeWithTooltip,
} from 'shared-components';
import { AgentApplicantAuthorisationStatus } from 'api-paths-cas';
import { ApplicationState } from '../../../store';
import { getApplicationOfferBadgeProps } from '../../../utilities/getApplicationOfferBadgeProps';
import ProgrammeDetailsModal from '../../pages/applicant-dashboard/programmes/ProgrammeDetailsModal';
import OfferStatusBadge from '../offerStatusBadge';
import { SectionProps } from './types';

interface Props {
  uni: string;
  userId: string;
  applicationId: string;
}

function ProgrammesLeftNav({ uni, userId, applicationId }: Props): JSX.Element {
  const [activeSection, setActiveSection] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { offers, agentAccessStatus } = useSelector((state: ApplicationState) => state.apply);
  const role = useSelector(getRole);

  const sections: SectionProps[] = useMemo((): SectionProps[] => {
    return offers?.map(offer => {
      // Extract all tasks from hierarchical requirements
      const allTasks = getAllTasksFromRequirements(offer?.requirements);
      // Build sublabel: [Mode], [Campus], [Intake], [Entry year]
      const campusText = offer.programme?.campus ? `, ${offer.programme.campus}` : '';
      const entryPointText = offer.programme?.entryPoint ? `, ${offer.programme.entryPoint} entry` : '';
      return {
        id: offer.id,
        label: offer.programme?.courseName || '',
        sublabel: `${offer.programme?.mode || ''}${campusText}, ${formatYearMonthDate(
          offer.intakeId,
          true,
        )}${entryPointText}`,
        status: offer.status,
        applicantStatus: offer.applicantStatus,
        conditionType: offer.conditionType,
        hasBeenEdited:
          Object.keys(offer?.pendingChanges ?? {}).length > 0 ||
          allTasks.some(task => Object.keys(task?.pendingChanges ?? {}).length > 0 && !task.lastUpdatedByApplicant),
        hasBeenEditedByApplicant: allTasks.some(
          task => Object.keys(task?.pendingChanges ?? {}).length > 0 && task.lastUpdatedByApplicant,
        ),
        route: `/application/${uni}/${userId}/${applicationId}/offers/${offer.id}`,
        routeRegex: new RegExp(`^/application/([^/]+)/([^/]+)/([^/]+)/offers/${offer.id}.*$`),
      };
    });
  }, [offers]);

  useEffect(() => {
    const sectionRoute = sections.find(i => i.routeRegex.test(location.pathname));
    setActiveSection(sectionRoute?.id);
  }, [location.pathname]);

  const getSectionRoute = (sectionId: string): string => {
    const section = sections.find(s => s.id === sectionId);
    return section?.route ?? '';
  };

  const isAgentAccessDeniedByAppliant = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED;

  const canEdit = hasPerm(role, P.EDIT_APPLY_OFFER) && !isAgentAccessDeniedByAppliant;

  return (
    <Box mt={4}>
      <CustomHeading
        as={'div'}
        display={'flex'}
        mb={1}
        pl={'sidebarPxSmall'}
        fontSize={'2xs'}
        lineHeight={4}
        color={'muted'}
        isAllCaps
      >
        Programmes
        {canEdit && (
          <Box ml={'auto'} my={-1}>
            <IconButtonWithTooltip
              label="Add new programme"
              onClick={onOpen}
              iconProps={{ icon: faCirclePlus }}
              buttonProps={{ variant: 'ghost', size: 'xs', isRound: true }}
            />
          </Box>
        )}
      </CustomHeading>

      {isOpen && <ProgrammeDetailsModal onClose={onClose} forceCreate={true} />}

      <Stack spacing={0} pb={10}>
        {sections.map((section, index) => {
          const badgeProps = getApplicationOfferBadgeProps(section.status);
          const applicantStatus = getStatusByConditionType(section?.applicantStatus, section?.conditionType);

          return (
            <Button
              as={RouterLink}
              to={getSectionRoute(section.id)}
              key={`programme-card-${index}`}
              isActive={section.id === activeSection}
              variant="sidebar"
              size={'sm'}
              py={2}
              rowGap={1}
              alignItems="start"
              flexDirection="column"
              overflowWrap="break-word"
              whiteSpace="normal"
              textAlign="left"
              height="auto"
            >
              {section.label}

              <Box fontSize={'xs'} color={'muted'} fontWeight={'normal'}>
                {section.sublabel}
              </Box>

              <Flex justify="space-between" width="full">
                <Badge size={'xs'} colorScheme={badgeProps.colorScheme}>
                  {badgeProps.children}
                </Badge>
                <Flex gap={1}>
                  <OfferStatusBadge applicantStatus={applicantStatus} iconSize="xs" />

                  {section.hasBeenEdited && (
                    <RoundBadgeWithTooltip
                      icon={faPen}
                      label={OfferTooltipLabels.pendingChanges}
                      colorScheme="orange"
                      size="xs"
                    />
                  )}
                  {section.hasBeenEditedByApplicant && (
                    <RoundBadgeWithTooltip
                      icon={faFilePlus}
                      label={OfferTooltipLabels.pendingChangesByApplicant}
                      colorScheme="indigo"
                      size="xs"
                    />
                  )}
                </Flex>
              </Flex>
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}

export default ProgrammesLeftNav;
